import type { BlockingLine } from "@/lib/scheduling/schedule-slots"
import type { Cart } from "@/types"

/** The bag as the slots endpoint reads it: ids to send, names to show back. */
export type CartLineIndex = {
  itemOfferIds: string[]
  optionIds: string[]
  nameById: Map<string, string>
}

/**
 * Indexes the bag once: the offer and option ids the grid must intersect, and
 * the name each id carries, so a `blockingLine` can be named back to the
 * customer instead of quoting an id at them.
 */
export function indexCartLines(cart: Cart | null | undefined): CartLineIndex {
  const itemOfferIds = new Set<string>()
  const optionIds = new Set<string>()
  const nameById = new Map<string, string>()

  for (const line of cart?.items ?? []) {
    const offerId = line.itemOffer?.id
    if (offerId) {
      itemOfferIds.add(offerId)
      nameById.set(offerId, line.item?.name ?? "")
    }
    for (const [optionId, option] of Object.entries(line.itemOptions ?? {})) {
      if (!optionId) continue
      optionIds.add(optionId)
      nameById.set(optionId, option?.name ?? "")
    }
  }

  return {
    itemOfferIds: [...itemOfferIds],
    optionIds: [...optionIds],
    nameById,
  }
}

/** Names a blocking line from the bag index; null when nothing matches. */
export function nameBlockingLine(index: CartLineIndex, line: BlockingLine): string | null {
  const id = line.itemOfferId ?? line.optionId
  if (!id) return null
  return index.nameById.get(id) || null
}
